import { useCallback, useEffect, useRef } from 'react';

type FileType = 'csv' | 'json' | 'python' | 'txt';

/**
 * Creates a stable download callback that always uses the latest file content.
 * Eliminates the common ref + effect + callback pattern from preview hooks.
 *
 * @param fileContent - The content to download (may change over time)
 * @param downloadFileContent - Function that performs the actual download
 * @param fileType - The type of file being downloaded
 * @returns A stable downloadFile callback
 */
export const useStableDownloadHandler = (
  fileContent: string | undefined,
  downloadFileContent: (content: string, fileType: FileType) => void,
  fileType: FileType
) => {
  const fileContentRef = useRef(fileContent);

  useEffect(() => {
    fileContentRef.current = fileContent;
  }, [fileContent]);

  const downloadFile = useCallback(() => {
    if (fileContentRef.current) {
      downloadFileContent(fileContentRef.current, fileType);
    }
  }, [downloadFileContent, fileType]);

  return downloadFile;
};

export default useStableDownloadHandler;
